import { colourHex } from '../../lib/format.ts';
import { cssVars } from '../../lib/css.ts';
import { ListPlus, Plus, Refresh, Trash, ZoomIn } from '../../lib/icons.tsx';
import LibraryItemMenu, { type LibraryItemActions } from './LibraryItemMenu.tsx';
import { libraryItemBadge, libraryItemMetaLine } from './libraryItemMeta.ts';
import type { Dispatch, SetStateAction } from 'react';
import type { LibraryItem } from '../../../shared/api.ts';

/** An item's first slide, opened big. `slug` is the item's own deck, and
 *  `idxs` its slides, which is all of that deck's now that an item owns one,
 *  so the modal's ‹ › has nowhere else to wander. */
export type ZoomTarget = { slug: string; idx: number; title: string; idxs: number[] };

/** The cover machinery the tile borrows from the page: the rendered cover for
 *  an item, what is on its way, what gave up, and how to ask again. Passed as
 *  one object because all five come from the same hook and a tile has no
 *  business owning any of them. Keyed by the item's SLUG throughout: an item
 *  is a deck, and its preview is that deck's cover (`lib/coverWarm.ts`). */
export type LibraryTileThumbs = {
  /** The rendered cover for an item, if there is one yet. */
  urlFor: (item: LibraryItem) => string | null;
  /** Registers this tile with the viewport observer: scrolling to it IS the
   *  request for its cover. */
  observe: (slug: string) => (el: Element | null) => void;
  coming: (slug: string) => boolean;
  isStuck: (slug: string) => boolean;
  retry: (slug: string) => void;
};

type LibraryTileProps = {
  item: LibraryItem;
  /** The name behind `item.ownerId`, when the people registry knows it. */
  owner: string | null;
  /** A server-side build is still writing this item's slides (an import). */
  building: boolean;
  /** The last build into this item failed, and what it said. */
  failure: string | null;
  busy: boolean;
  menuOpen: boolean;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  actions: LibraryItemActions;
  onZoom: (target: ZoomTarget) => void;
  thumbs: LibraryTileThumbs;
};

/**
 * One library item as a card: first-slide preview, the series badge, the name
 * with its accent dot, the meta line, and a row of three actions.
 *
 * Restyled to the design in Run 10 (2026-09-07). What that run had to protect
 * is every affordance the tile already carried, because the frame shows none of
 * them: clicking the preview still opens the item in the editor overlay, the
 * corner magnifier still opens a live frame, the kebab still holds all six
 * verbs, and an item with no slides yet still gets its "Add the first slide"
 * arm. The three new buttons SIT BESIDE those rather than replacing them.
 *
 * **Insert opens a deck picker.** An insert needs a deck to insert INTO, and
 * this page has none, so the button asks which one (`InsertIntoDeckModal`) and
 * then calls the same route the editor's Add-a-slide gallery does.
 *
 * **Preview and the corner magnifier are the same action, deliberately twice.**
 * The magnifier is the affordance that exists today and appears on hover, which
 * a touch pointer never does; the named button is the discoverable one the
 * design asks for. Removing either would be a regression for one of the two
 * kinds of user.
 */
export default function LibraryTile({
  item,
  owner,
  building,
  failure,
  busy,
  menuOpen,
  setMenuKey,
  actions,
  onZoom,
  thumbs,
}: LibraryTileProps) {
  const first = item.slides[0] ?? null;
  const slideIdxs = item.slides.map((s) => s.idx);
  if (!first) {
    // A just-created item with no slides yet — not offered to decks until it
    // has content. While a build is writing into it the tile says so instead
    // of offering "Add the first slide" on top of the slides landing; a build
    // that FAILED says that too, because a tile that went quiet reads as
    // "nothing happened", which is the one thing that definitely did not.
    return (
      <li className="library-tile">
        <button
          type="button"
          className="library-tile__frame library-tile__frame--empty"
          onClick={() => !building && actions.onAddSlide(item)}
          disabled={building}
          aria-label={
            building
              ? `${item.name}: writing slides`
              : failure
                ? `${item.name}: the import failed: ${failure}. Add the first slide yourself`
                : `Add the first slide to ${item.name}`
          }
        >
          <span className="library-tile__empty">
            {building ? (
              <>
                <span className="new-deck__spinner new-deck__spinner--inline" aria-hidden="true" /> Writing slides…
              </>
            ) : (
              <>
                <Plus /> Add the first slide
              </>
            )}
          </span>
        </button>
        <div className="library-tile__meta">
          <span className="library-tile__title" title={item.name}>
            <span className="library__cat-dot" style={{ background: colourHex(item.color) }} aria-hidden="true" />
            {item.name}
          </span>
          <span className="library-tile__stats">
            {building ? 'Import in progress' : failure ? `The import failed: ${failure}` : 'No slides yet'}
          </span>
        </div>
        <div className="library-tile__row">
          <span className="library-tile__actions">
            <button
              type="button"
              className="iconbtn"
              onClick={() => actions.onDelete(item)}
              aria-label={`Delete ${item.name}`}
              title="Delete"
              disabled={busy || building}
            >
              <Trash />
            </button>
          </span>
        </div>
      </li>
    );
  }
  const url = thumbs.urlFor(item);
  const badge = libraryItemBadge(item);
  const zoom = () => onZoom({ slug: item.slug, idx: first.idx, title: item.name, idxs: slideIdxs });
  return (
    <li className="library-tile">
      <button
        type="button"
        className="library-tile__frame"
        onClick={() => actions.onEdit(item)}
        aria-label={`Edit ${item.name}`}
      >
        {/* The ref is what asks for this tile's cover: scrolling to it IS the
            request. Attached only while there is no cover, which is the cover
            queue's contract: a card that has its picture has nothing left to
            ask for, and leaving it observed would keep its deck queued. */}
        <span className="library-tile__shot" ref={url ? undefined : thumbs.observe(item.slug)}>
          {url ? (
            <img className="library-tile__img" src={url} alt="" loading="lazy" />
          ) : thumbs.isStuck(item.slug) ? (
            // Failed, or the wait ran out. The way back is on the tile: this
            // page has no deck-wide refresh control at all.
            <span
              className="library-tile__retry"
              role="button"
              tabIndex={0}
              title="This preview didn't render. Try again."
              aria-label={`Retry the preview for ${item.name}`}
              onClick={(e) => {
                e.stopPropagation();
                thumbs.retry(item.slug);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  thumbs.retry(item.slug);
                }
              }}
            >
              <Refresh size={16} />
            </span>
          ) : (
            // Shimmers only while a cover is genuinely on the way. A ghost
            // that animates for a deck nobody is rendering promises something
            // that is not coming.
            //
            // Tinted with the item's own accent so a waiting shelf reads as a
            // set of categorised things rather than a grid of grey holes. Free:
            // the colour is already here, no image has to be made for it.
            <span
              className={'library-tile__skeleton' + (thumbs.coming(item.slug) ? ' is-working' : '')}
              aria-hidden="true"
              style={item.color ? cssVars({ '--tile-accent': colourHex(item.color) }) : undefined}
            />
          )}
        </span>
        {badge && <span className="library-tile__badge">{badge}</span>}
      </button>
      {/* A SIBLING of the tile button (a nested button is invalid markup).
          The tile is a 640x360 raster, so reading it means a live frame. */}
      <button
        type="button"
        className="library-tile__zoom"
        title="Take a closer look"
        aria-label={`Preview ${item.name}`}
        onClick={(e) => {
          e.stopPropagation();
          // A multi-slide item walks its OWN slides, which are all its deck
          // holds. Built at click time so the array identity stays stable
          // while the modal is open (it feeds a keydown effect).
          zoom();
        }}
      >
        <ZoomIn />
      </button>
      <div className="library-tile__meta">
        <span className="library-tile__title" title={item.name}>
          <span className="library__cat-dot" style={{ background: colourHex(item.color) }} aria-hidden="true" />
          {item.name}
        </span>
        {/* One line, dropping the clauses it has no answer for — see
            `libraryItemMeta.ts`. */}
        <span className="library-tile__stats">{libraryItemMetaLine(item, undefined, owner)}</span>
      </div>
      <div className="library-tile__row">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          // Named for THIS item: a grid of twenty cards otherwise announces
          // "Insert, button" twenty times with nothing to tell them apart.
          aria-label={`Insert ${item.name} into a deck`}
          onClick={() => actions.onInsert(item)}
          disabled={busy}
        >
          <ListPlus /> Insert
        </button>
        <button type="button" className="btn btn--ghost btn--sm" aria-label={`Preview ${item.name}`} onClick={zoom}>
          Preview
        </button>
        <span className="library-tile__actions">
          <LibraryItemMenu item={item} menuOpen={menuOpen} busy={busy} setMenuKey={setMenuKey} actions={actions} />
        </span>
      </div>
    </li>
  );
}
